import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NonLinearStepper } from '~/components/non-linear-stepper/NonLinearStepper';
import { useThemeStore } from '~/components/theme-controller/stores/useThemeStore';
import { CompetingThemes } from '~/modules/overview-knowledge-graph/components/competing-themes/CompetingThemes';
import { RelativePerformance } from '~/modules/overview-knowledge-graph/components/relative-performance/RelativePerformance';
import { CompetitorData } from '~/widgets/knowledge-graph-widget/hooks/useCompetitorsData';
import sharedStyles from '~/widgets/theme-widget/tabs/components/stepper-section/StepperSection.module.scss';
import { RankSection } from '~/widgets/theme-widget/tabs/theme-exposure-tab/components/rank-section/RankSection';

import styles from './CompetitorOverview.module.scss';

export const CompetitorOverview = ({
  entity,
  competitor,
}: {
  entity: Entity;
  competitor: CompetitorData;
}) => {
  const { t } = useTranslation(['widget']);
  const { getThemedLogoUrl } = useThemeStore();

  const stepperTitle = t(
    'assetOverview.knowledgeGraph.competitors.tabs.competitorOverview.header',
    { name: entity.name_short || entity.name }
  );

  const {
    competitorData,
    entity: competitorEntity,
    competingThemes,
  } = competitor;

  return (
    <div className={styles.root}>
      <div className={sharedStyles.sectionRow} data-testid="stepper-section">
        <div className={sharedStyles.headerWrapper}>
          <div className={sharedStyles.titleMedium}>{stepperTitle}</div>
        </div>
        <div className={sharedStyles.stepperWrapper}>
          <NonLinearStepper
            activeIndex={0}
            iconSize={18}
            steps={[
              {
                logoUrl: getThemedLogoUrl(entity),
                hoverLabel: entity.name_short || entity.name,
                connectorLabel: t(
                  'assetOverview.knowledgeGraph.competitors.tabs.competitorOverview.connectorLabel'
                ),
                highlightConnector: true,
                isSimple: true,
              },
              {
                logoUrl: getThemedLogoUrl(competitorEntity),
                hoverLabel:
                  competitorEntity.name_short || competitorEntity.name,
                highlightConnector: true,
              },
            ]}
          />
        </div>
        <div className={sharedStyles.bodySmall}>
          {competitorData.relationship.description}
        </div>
      </div>
      <RankSection
        title={t(
          'assetOverview.knowledgeGraph.competitors.tabs.competitorOverview.rank'
        )}
        label={t(
          'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.current'
        )}
        rank={competitorData.relationship.rank}
      />
      <CompetingThemes competingThemes={competingThemes} />
      <RelativePerformance entity={entity} />
    </div>
  );
};
